// Funktionen & Vorlagen je Kunde: Freischaltung aus /auth/me (Menü, Studio, Galerie) und Plattform-Abfragen.
// Spiegel von apps/core/src/modules/features/catalog.ts (Schlüssel der Module und Bausteine).
import { useQuery } from '@tanstack/react-query';
import { api } from './client';
import type { Me } from './types';

export type FeatureKey =
  | 'prayer' | 'cameras' | 'web' | 'datasources' | 'datasourceFeed' | 'datasourceCalendar' | 'datasourceTable'
  | 'reports' | 'emergency' | 'approval' | 'monitoring' | 'power';

/** Studio-Bausteine (Elementtypen) je Funktion. */
export const WIDGET_FEATURE: Record<string, FeatureKey> = {
  prayer: 'prayer', camera: 'cameras', web: 'web', feed: 'datasourceFeed', calendar: 'datasourceCalendar', table: 'datasourceTable',
};
const PARENT: Partial<Record<FeatureKey, FeatureKey>> = { datasourceFeed: 'datasources', datasourceCalendar: 'datasources', datasourceTable: 'datasources' };

/** Funktion freigeschaltet? Ohne Angabe (ältere Server, kein Mandant) = ja. */
export function hasFeature(me: Me | null | undefined, k: FeatureKey): boolean {
  const m = me?.features?.modules;
  if (!m) return true;
  const parent = PARENT[k];
  return m[k] !== false && (!parent || m[parent] !== false);
}

export const categoryAllowed = (me: Me | null | undefined, c: string): boolean => me?.features?.categories?.[c] !== false;

export const widgetAllowed = (me: Me | null | undefined, type: string): boolean => {
  const k = WIDGET_FEATURE[type];
  return !k || hasFeature(me, k);
};

/** Elementtypen einer Definition, deren Funktion abgeschaltet ist (Hinweis „läuft weiter"). */
export function disabledWidgets(me: Me | null | undefined, definition: unknown): string[] {
  const els = (definition as { elements?: { type?: string }[] } | null)?.elements;
  if (!Array.isArray(els)) return [];
  return [...new Set(els.map((e) => e?.type ?? '').filter((t) => t && !widgetAllowed(me, t)))];
}

// ---------- Plattform ----------

export type Toggles = Record<string, boolean>;
export interface FeatureCatalog {
  modules: { key: FeatureKey; parent: FeatureKey | null; widgets: string[] }[];
  categories: { key: string; name: { de: string; en: string }; color: string | null }[];
  presets: { key: string; name: { de: string; en: string }; industry: string | null; modules: Toggles; categories: Toggles }[];
}
export interface TenantFeatures {
  tenantId: string; configured: boolean; industry: string | null; preset: string | null; modules: Toggles; categories: Toggles; updatedAt: string | null;
}
export interface GlobalTemplateAccess { id: string; name: string; orientation: 'landscape' | 'portrait'; category: string | null; tenantIds: string[] }

export const qkFeatures = {
  catalog: ['feature-catalog'] as const,
  tenant: (id: string) => ['tenant-features', id] as const,
  templates: ['global-templates'] as const,
};

export const useFeatureCatalog = () => useQuery({ queryKey: qkFeatures.catalog, queryFn: () => api.get<FeatureCatalog>('/platform/features'), staleTime: 300_000 });
export const useTenantFeatures = (id: string) => useQuery({ queryKey: qkFeatures.tenant(id), queryFn: () => api.get<TenantFeatures>(`/tenants/${id}/features`) });
export const useGlobalTemplates = () => useQuery({ queryKey: qkFeatures.templates, queryFn: async () => (await api.get<{ items: GlobalTemplateAccess[] }>('/platform/global-templates')).items });
